import { DataSource, CollectionViewer } from '@angular/cdk/collections';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatSort } from '@angular/material/sort';
import { map } from 'rxjs/operators';
import { Observable, of as observableOf, merge } from 'rxjs';

export interface EmployeeListItem {
  firstName: string;
  lastName: string;
  email: string;
  appRoles: Array<string>;
  id: string;
  principalId: string;
  softDelete: boolean;
}

export interface Employee {
  firstName: string;
  lastName: string;
  userName: string;
  employeeId?: string;
  azureAdId: string;
  email: string;
}

export class EmployeeListDataSource extends DataSource<any> {
  data: Array<any>;
  paginator: MatPaginator;
  sort: MatSort;
  nonFilteredData;

  constructor(private employees: Array<any>) {
    super();
    this.data = employees;
    this.nonFilteredData = employees;
  }

  /**
   * Connect this data source to the table. The table will only update when
   * the returned stream emits new items.
   * @returns A stream of the items to be rendered.
   */
  connect(): Observable<any[]> {
    // Combine everything that affects the rendered data into one update
    // stream for the data-table to consume.
    const dataMutations = [
      observableOf(this.data),
      this.paginator.page,
      this.sort.sortChange
    ];

    return merge(...dataMutations).pipe(map(() => {
      return this.getPagedData(this.getSortedData([...this.data]));
    }));
  }

  /**
   *  Called when the table is being destroyed. Use this function, to clean up
   * any open connections or free any held resources that were set up during connect.
   */
  disconnect() { }

  filter(field: any, fullData: any) {

    let filterApplied = false;
    let filterData: any[] = fullData;
    if (field['firstNameFilter']) {
      filterData = filterData.filter(x => {
        return x.firstName.toLocaleLowerCase().indexOf(field['firstNameFilter'].toLocaleLowerCase()) > -1;
      });
    } if (field['lastNameFilter']) {
      filterData = filterData.filter(x => {
        return x.lastName.toLocaleLowerCase().indexOf(field['lastNameFilter'].toLocaleLowerCase()) > -1;
      });
    } if (field['emailFilter']) {
      filterData = filterData.filter(x => {
        return x.email.toLocaleLowerCase().indexOf(field['emailFilter'].toLocaleLowerCase()) > -1;
      });
    } if (Number.parseInt(field['employeeStatusFilter']?.toString()) >= 0) {
      filterData = filterData.filter(x => {
        return x.softDelete == field['employeeStatusFilter'];
      });
    } if (field['appRoleFilter']) {
      filterData = filterData.filter(x => {
        return x.appRoles.find((x: any) => x.id == field.appRoleFilter);
      });
    }
    return filterData;
  }

  /**
   * Paginate the data (client-side). If you're using server-side pagination,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getPagedData(data: any[]) {
    const startIndex = this.paginator.pageIndex * this.paginator.pageSize;
    return data.splice(startIndex, this.paginator.pageSize);
  }

  /**
   * Sort the data (client-side). If you're using server-side sorting,
   * this would be replaced by requesting the appropriate data from the server.
   */
  private getSortedData(data: any[]) {
    if (!this.sort.active || this.sort.direction === '') {
      return data;
    }

    return data.sort((a, b) => {
      const isAsc = this.sort.direction === 'asc';
      switch (this.sort.active) {
        case 'firstName': return compare(a.firstName, b.firstName, isAsc);
        case 'lastName': return compare(a.lastName, b.lastName, isAsc);
        case 'email': return compare(a.email, b.email, isAsc);
        default: return 0;
      }
    });
  }

}
/** Simple sort comparator for example ID/Name columns (for client-side sorting). */
function compare(a: string | number, b: string | number, isAsc: boolean) {
  return (a < b ? -1 : 1) * (isAsc ? 1 : -1);
}
